/**
 * OnboardingLayout
 *
 * A world-class onboarding experience with editorial design sensibility.
 * Inspired by Collins, Pentagram, and Clay design studios.
 *
 * Design Philosophy:
 * - Quiet luxury with intentional typography
 * - Generous negative space
 * - Sophisticated motion choreography
 * - Editorial-grade visual hierarchy
 */

import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  stepProgressAtom,
  canSkipOnboardingAtom,
  currentOnboardingStepAtom,
} from '../../state/onboardingAtoms';

interface OnboardingLayoutProps {
  children: ReactNode;
  showBack?: boolean;
  onBack?: () => void;
  showSkip?: boolean;
  onSkip?: () => void;
  showProgress?: boolean;
  contentClassName?: string;
}

// Step labels for the progress indicator
const STEP_LABELS = ['Welcome', 'Questions', 'Stage', 'Result', 'Personalize'];

export function OnboardingLayout({
  children,
  showBack = false,
  onBack,
  showSkip = true,
  onSkip,
  showProgress = true,
  contentClassName = '',
}: OnboardingLayoutProps) {
  const navigate = useNavigate();
  const { current, total, percentage } = useAtomValue(stepProgressAtom);
  const canSkip = useAtomValue(canSkipOnboardingAtom);
  const currentStep = useAtomValue(currentOnboardingStepAtom);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const shouldShowSkip = showSkip && canSkip;

  return (
    <div className="min-h-dvh bg-alabaster flex flex-col relative overflow-hidden">
      {/* Subtle grain texture overlay */}
      <div className="noise pointer-events-none fixed inset-0 z-0" />

      {/* Architectural grid background - very subtle */}
      <div
        className="fixed inset-0 pointer-events-none z-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #292524 1px, transparent 1px),
            linear-gradient(to bottom, #292524 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Header */}
      <header className="relative z-10 safe-area-pt">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Left: Back button or Logo */}
            <div className="w-32">
              <AnimatePresence mode="wait">
                {showBack ? (
                  <motion.button
                    key="back"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.2 }}
                    onClick={handleBack}
                    className="group flex items-center gap-2 text-sand hover:text-charcoal transition-colors duration-300"
                    aria-label="Go back"
                  >
                    <span className="size-10 flex items-center justify-center rounded-full border border-stone/50 group-hover:border-charcoal/30 transition-colors duration-300">
                      <ArrowLeft className="size-4" />
                    </span>
                    <span className="text-sm font-sans hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Back
                    </span>
                  </motion.button>
                ) : (
                  <motion.div
                    key="logo"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center"
                  >
                    <span className="font-serif text-xl font-medium text-charcoal">
                      VSP
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Center: Progress indicator */}
            {showProgress && (
              <div className="hidden sm:flex items-center gap-3">
                {STEP_LABELS.slice(0, total).map((label, index) => {
                  const stepNum = index + 1;
                  const isActive = stepNum === current;
                  const isCompleted = stepNum < current;

                  return (
                    <div key={label} className="flex items-center gap-3">
                      <motion.div
                        className="flex items-center gap-2"
                        animate={{
                          opacity: isActive ? 1 : isCompleted ? 0.7 : 0.4,
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <div
                          className={cn(
                            'size-2 rounded-full transition-all duration-500',
                            isActive && 'bg-olive scale-125',
                            isCompleted && 'bg-olive',
                            !isActive && !isCompleted && 'bg-stone'
                          )}
                        />
                        <span
                          className={cn(
                            'text-xs font-sans uppercase tracking-wider transition-colors duration-300',
                            isActive ? 'text-charcoal' : 'text-sand'
                          )}
                        >
                          {label}
                        </span>
                      </motion.div>
                      {index < total - 1 && (
                        <div
                          className={cn(
                            'w-8 h-px transition-colors duration-500',
                            isCompleted ? 'bg-olive/50' : 'bg-stone/50'
                          )}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Right: Skip button */}
            <div className="w-32 flex justify-end">
              <AnimatePresence>
                {shouldShowSkip && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={handleSkip}
                    className="group flex items-center gap-2 text-sand hover:text-charcoal transition-colors duration-300"
                    aria-label="Skip to dashboard"
                  >
                    <span className="text-sm font-sans opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden sm:inline">
                      Skip
                    </span>
                    <span className="size-10 flex items-center justify-center rounded-full border border-stone/50 group-hover:border-charcoal/30 transition-colors duration-300">
                      <X className="size-4" />
                    </span>
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Mobile progress bar */}
        {showProgress && (
          <div className="sm:hidden px-6">
            <div className="flex items-center justify-between mb-2">
              <span className="micro-copy">
                Step {current} of {total}
              </span>
              <span className="micro-copy">
                {Math.round(percentage)}%
              </span>
            </div>
            <div className="h-0.5 bg-stone/30 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-olive rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className={cn('flex-1 flex flex-col relative z-10', contentClassName)}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: mounted ? 1 : 0, y: mounted ? 0 : 24 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="flex-1 flex flex-col"
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 safe-area-pb">
        <div className="text-center">
          <span className="micro-copy opacity-40">
            VirtualStagePro
          </span>
        </div>
      </footer>
    </div>
  );
}

/**
 * OnboardingStepContainer
 * Container for individual step content with generous spacing.
 */
interface OnboardingStepContainerProps {
  children: ReactNode;
  centered?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
}

export function OnboardingStepContainer({
  children,
  centered = true,
  maxWidth = '2xl',
}: OnboardingStepContainerProps) {
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
  };

  return (
    <div
      className={cn(
        'flex-1 w-full mx-auto px-6 sm:px-8 lg:px-12 py-8 sm:py-12',
        maxWidthClasses[maxWidth],
        centered && 'flex flex-col justify-center'
      )}
    >
      {children}
    </div>
  );
}

/**
 * OnboardingHeading
 * Editorial-style heading with refined typography.
 */
interface OnboardingHeadingProps {
  title: string;
  subtitle?: string;
  centered?: boolean;
  eyebrow?: string;
}

export function OnboardingHeading({
  title,
  subtitle,
  centered = true,
  eyebrow,
}: OnboardingHeadingProps) {
  return (
    <div className={cn('mb-10 sm:mb-12', centered && 'text-center')}>
      {eyebrow && (
        <motion.span
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="micro-copy mb-4 block"
        >
          {eyebrow}
        </motion.span>
      )}
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className="headline-editorial text-3xl sm:text-4xl lg:text-5xl text-balance leading-tight"
      >
        {title}
      </motion.h1>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            'text-sand font-sans text-lg sm:text-xl mt-4 text-pretty leading-relaxed',
            centered && 'max-w-xl mx-auto'
          )}
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}

/**
 * OnboardingButton
 * Primary action button with sophisticated hover states.
 */
interface OnboardingButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  fullWidth?: boolean;
  className?: string;
  size?: 'default' | 'lg';
}

export function OnboardingButton({
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  variant = 'primary',
  fullWidth = false,
  className = '',
  size = 'default',
}: OnboardingButtonProps) {
  const baseClasses = cn(
    'inline-flex items-center justify-center gap-3 font-sans font-medium',
    'rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
    size === 'default' && 'px-8 py-4 text-base',
    size === 'lg' && 'px-10 py-5 text-lg'
  );

  const variantClasses = {
    primary: cn(
      'bg-charcoal text-alabaster',
      'hover:-translate-y-0.5 hover:shadow-lift',
      'active:translate-y-0 active:shadow-soft',
      'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none'
    ),
    secondary: cn(
      'bg-transparent text-charcoal border-2 border-charcoal/20',
      'hover:border-charcoal/40 hover:-translate-y-0.5',
      'active:translate-y-0',
      'disabled:opacity-40 disabled:cursor-not-allowed'
    ),
    ghost: cn(
      'text-sand hover:text-charcoal',
      'disabled:opacity-40 disabled:cursor-not-allowed'
    ),
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        baseClasses,
        variantClasses[variant],
        fullWidth && 'w-full',
        className
      )}
    >
      {loading ? (
        <>
          <span className="size-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>Processing</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * OnboardingCard
 * Selectable card with refined interaction states.
 */
interface OnboardingCardProps {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export function OnboardingCard({
  children,
  selected = false,
  onClick,
  disabled = false,
  className = '',
}: OnboardingCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'relative w-full p-6 sm:p-8 rounded-3xl text-left transition-all duration-300',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
        selected
          ? 'bg-white border-2 border-olive shadow-soft'
          : 'bg-white/60 border-2 border-transparent hover:bg-white hover:border-stone/50 hover:shadow-soft',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {children}

      {/* Selection indicator */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
            className="absolute top-4 right-4 size-6 bg-olive rounded-full flex items-center justify-center"
          >
            <svg className="size-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
}

/**
 * OnboardingDivider
 * Subtle visual separator.
 */
export function OnboardingDivider({ text }: { text?: string }) {
  if (text) {
    return (
      <div className="flex items-center gap-4 my-8">
        <div className="flex-1 h-px bg-stone/30" />
        <span className="micro-copy">{text}</span>
        <div className="flex-1 h-px bg-stone/30" />
      </div>
    );
  }

  return <div className="h-px bg-stone/30 my-8" />;
}
